import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import StatsBanner from "../components/StatsBanner";
import ServerFilters from "../components/ServerFilters";
import ServerList from "../components/ServerList";
import ServerDetailModal from "../components/ServerDetailModal";
import FavoritesSheet from "../components/FavoritesSheet";
import CustomMcPanel from "../components/CustomMcPanel";
import { useGame } from "../context/GameContext";
import { fetchServer } from "../lib/api";

const DEFAULT_FILTERS = {
  q: "",
  region: "all",
  mode: "all",
  map: "all",
  modifier: "all",
  official: "all",
  min_players: 0,
  max_ping: 300,
  online_only: true,
  favorites_only: false,
  pin_favorites: true,
  sort: "players",
  order: "desc",
  page: 1,
  page_size: 25,
};

const ServerBrowser = () => {
  const { game, gameId, setGameId, isLoading: gamesLoading, isError: gamesError, refetch: refetchGames, games } = useGame();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selected, setSelected] = useState(null);
  const [listResponse, setListResponse] = useState(null);
  const [favOpen, setFavOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: ?server=ID&game=GID → open modal for that server
  const sharedServerId = searchParams.get("server");
  const sharedGameId = searchParams.get("game");

  useEffect(() => {
    if (sharedGameId && sharedGameId !== gameId) setGameId(sharedGameId);
  }, [sharedGameId]); // eslint-disable-line

  const { data: sharedServer } = useQuery({
    queryKey: ["shared-server", sharedServerId],
    queryFn: () => fetchServer(sharedServerId),
    enabled: !!sharedServerId,
    retry: 1,
  });

  useEffect(() => {
    if (sharedServer) setSelected(sharedServer);
  }, [sharedServer]);

  // Reset filters when game changes
  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
  }, [gameId]);

  const closeModal = () => {
    setSelected(null);
    if (sharedServerId) {
      searchParams.delete("server");
      searchParams.delete("game");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="min-h-screen" data-testid="server-browser-page">
      <TopNav onOpenFavorites={() => setFavOpen(true)} />
      <main className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        {/* Games fetch failed (slow backend / cold start). Show a retry card
            so the page is never visually blank. */}
        {gamesError && !games.length && (
          <div
            className="rounded-md border px-4 py-6 text-center space-y-3"
            style={{ borderColor: "var(--primary-accent)", background: "rgba(0,0,0,0.3)" }}
            data-testid="games-error-card"
          >
            <div className="hud-caption" style={{ color: "var(--primary-accent)" }}>CONNECTION ISSUE</div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Couldn't load the games list. The backend may be waking up — this usually takes a few seconds.
            </p>
            <button
              onClick={() => refetchGames()}
              className="px-4 py-2 border font-bold tracking-widest uppercase text-xs"
              style={{ color: "var(--primary-accent)", borderColor: "var(--primary-accent)" }}
              data-testid="games-retry-btn"
            >
              Retry
            </button>
          </div>
        )}

        {/* Initial load skeleton — keeps the layout visible so the user sees
            progress instead of a blank screen while /api/games resolves. */}
        {gamesLoading && !games.length && !gamesError && (
          <div className="space-y-4 animate-pulse" data-testid="games-loading-skeleton">
            <div className="h-10 w-72 rounded" style={{ background: "rgba(255,255,255,0.05)" }} />
            <div className="h-24 rounded" style={{ background: "rgba(255,255,255,0.04)" }} />
            <div className="h-96 rounded" style={{ background: "rgba(255,255,255,0.03)" }} />
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-3 rise">
          <div className="min-w-0">
            <div className="hud-caption flex items-center gap-2">
              GAME SERVER BROWSER
              {game?.source === "mock_demo" && (
                <span
                  className="px-1.5 py-0.5 border font-bold tracking-widest"
                  style={{ color: "var(--secondary-accent)", borderColor: "var(--secondary-accent)" }}
                  data-testid="demo-badge"
                >
                  DEMO · SIMULATED DATA
                </span>
              )}
              {game?.source && game.source !== "mock_demo" && (
                <span
                  className="px-1.5 py-0.5 border font-bold tracking-widest"
                  style={{ color: "var(--highlight)", borderColor: "var(--highlight)" }}
                >
                  LIVE DATA
                </span>
              )}
            </div>
            <h1
              className="font-head text-3xl md:text-5xl font-extrabold uppercase truncate"
              style={{ color: "var(--text-primary)", letterSpacing: "0.02em", lineHeight: 1.15 }}
              data-testid="page-title"
            >
              {game?.name || "Loading..."}
              <span style={{ color: "var(--primary-accent)" }}>.</span>
            </h1>
            <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>
              Live listing · Filter, search, favorite and share every battle in real time.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2 hud-caption">
            <span className="pulse-dot" />
            REALTIME TELEMETRY · AUTO REFRESH 25s
          </div>
        </div>

        <StatsBanner />

        <CustomMcPanel />

        <ServerFilters
          filters={filters}
          setFilters={setFilters}
          resultCount={listResponse?.total}
          options={{
            regions: listResponse?.regions || [],
            modes: listResponse?.modes || [],
            maps: listResponse?.maps || [],
            modifiers: listResponse?.modifiers || [],
          }}
        />

        <ServerList
          filters={filters}
          setFilters={setFilters}
          onOpenServer={setSelected}
          onDataChange={setListResponse}
        />

        <ServerDetailModal
          server={selected}
          open={!!selected}
          onClose={closeModal}
        />

        <FavoritesSheet
          open={favOpen}
          onOpenChange={setFavOpen}
          onOpenServer={setSelected}
        />

        <footer className="pt-4 pb-10 text-center hud-caption">
          WARDOGS SERVER NETWORK · MULTI-GAME BROWSER · POWERED BY STEAM / BATTLEBIT / MCSRVSTAT.US
        </footer>
      </main>
    </div>
  );
};

export default ServerBrowser;
